import { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { saveGift } from "@/lib/cart";
import { deviceId, gameDay, GAME_RESULT, GAME_SEEN } from "@/lib/game";
import { BagPicker } from "./BagPicker";
import { RipReveal } from "./RipReveal";
import { ContactForm } from "./ContactForm";
import { WinPanel } from "./Panels";

const API = process.env.REACT_APP_BACKEND_URL;
// a saved result only counts for the day it was played; a new day means a new bag
const readResult = () => {
  try {
    const r = JSON.parse(localStorage.getItem(GAME_RESULT));
    return r?.day === gameDay() ? r : null;
  } catch { return null; }
};

export const MysteryGame = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onGift = pathname.startsWith("/gift");
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 500);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  // lift above the mobile buy bar only while that bar is showing
  const raised = scrolled && pathname !== "/shop" && pathname !== "/build";
  const [open, setOpen] = useState(false);
  const [res, setRes] = useState(readResult);
  const [pick, setPick] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!API || onGift || localStorage.getItem(GAME_SEEN)) return undefined;
    const t = setTimeout(() => { setOpen(true); localStorage.setItem(GAME_SEEN, "1"); }, 7000);
    return () => clearTimeout(t);
  }, [onGift]);

  const store = (r) => { setRes(r); localStorage.setItem(GAME_RESULT, JSON.stringify(r)); };
  const applyPrize = (r, mail) => {
    if (r.code) saveGift({ code: r.code });
    else if (r.note) saveGift({ note: `${r.note} (${mail || "mystery game"})` });
  };

  const play = async (form) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/game/play`, { ...form, pick, device_id: deviceId() });
      store(r.data);
      if (r.data?.already_played) toast("You've already opened a mystery bag. Here's your gift!");
      applyPrize(r.data, form.email);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open your bag. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const step = res?.claimed ? "done" : pick ? "contact" : "pick";

  return (
    <>
      {!open && !onGift && !res?.claimed && API && (
        <button data-testid="mystery-gift-fab" onClick={() => setOpen(true)}
          className={`fixed ${raised ? "bottom-20" : "bottom-4"} md:bottom-6 left-4 z-40 h-12 px-5 rounded-full bg-[#FFB21A] text-[#1B1530] font-bold shadow-xl inline-flex items-center gap-2 hover:bg-[#FF5A5F] hover:text-white transition-[background-color,color,bottom] duration-300`}>
          <Gift className="h-4 w-4" /> Mystery gift
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="mystery-game-dialog" data-lenis-prevent className="max-w-xl w-[94vw] max-h-[92vh] overflow-y-auto rounded-[28px] bg-[#FFF6EA] border-0 p-5 sm:p-8">
          <p className="eyebrow text-[#FF5A5F]">Mystery gift</p>
          <DialogTitle className="font-display text-3xl sm:text-4xl leading-none">
            {step === "pick" && "Pick a bag. Win a gift."}
            {step === "contact" && "Almost there!"}
            {step === "done" && `You won ${res?.prize?.label}!`}
          </DialogTitle>
          <DialogDescription className="text-[#4A4560]">
            {step === "pick" && "Every bag hides a surprise: 10% to 20% off, a free bag, or the rare free 12-pack jackpot. Tap one to pick it."}
            {step === "contact" && "Enter your email and phone to rip your bag open and see what you won."}
            {step === "done" && "It's saved and will be applied automatically at checkout."}
          </DialogDescription>
          <div className="mt-5 space-y-5">
            {step === "pick" && <BagPicker onPick={setPick} disabled={busy} />}
            {step === "done" && <RipReveal pick={res.pick} prize={res.prize} others={res.others} />}
            {step === "contact" && (
              <>
                <ContactForm onSubmit={play} busy={busy} />
                <button data-testid="game-pick-again" type="button" onClick={() => setPick(null)} className="block mx-auto text-xs font-semibold text-[#4A4560] underline">Pick a different bag</button>
              </>
            )}
            {step === "done" && <WinPanel code={res.code} note={res.note} prizeKey={res.prize?.key} onShop={() => { setOpen(false); navigate("/build?size=12"); }} />}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
